import React, { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import { currencyValue } from "../utils";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import productIcon from "svg/products.svg";
import openIcon from "svg/open.svg";
import QuoteFloorProducts from "./components/QuoteFloorProducts";
import QuoteFloorLabor from "./components/QuoteFloorLabor";
import QuoteSummaryFields from "../common/quoteSummaryFields";
import Container from "react-bootstrap/Container";
import { api } from "store/slices/api";
import { toast } from "react-toastify";
import "./style.scss";

const FloorPlanDetails = () => {
  const { quoteId, floorId } = useParams();
  const navigate = useNavigate();
  const { data: quoteFields = {} } = api.useGetPriceQuoteQuery(quoteId);
  const { data: products = [] } = api.useGetFloorProductsQuery(floorId);
  const { data: laborCodes = [] } = api.useGetFloorLaborCodesQuery(floorId);
  const { data: floorData } = api.useGetPriceQuoteFloorQuery(floorId);
  const { floorNotes: floorNote } = floorData || {};
  const { data: floorPlanImageURL } = api.useGetPriceQuoteFloorImageQuery(floorData?.pictureURL, {
    skip: !floorData?.pictureURL,
  });
  const floorPlanImage =
    floorPlanImageURL || (floorData?.picture ? "data:image/jpeg;base64," + floorData?.picture : "");
  const [updateFloor] = api.useUpdatePriceQuoteFloorMutation();
  const [floorPlanNote, setFloorPlanNote] = useState("");
  const { storeCcn, terrCcn } = useSelector((state) => state.loggedInUser);
  const totalProductsAmt = products.reduce((prev, current) => prev + +current.extendedPrice, 0);
  const totalLaborAmt = laborCodes.reduce(function (prev, current) {
    return prev + +current.extendedPrice;
  }, 0);
  const totalProductsTaxAmt = products.reduce((prev, current) => prev + +current.salesTax, 0);
  const totalLaborTaxAmt = laborCodes.reduce(function (prev, current) {
    return prev + +current.salesTax;
  }, 0);
  const totalTaxAmt = totalProductsTaxAmt + totalLaborTaxAmt;
  const totalAmt = totalProductsAmt + totalLaborAmt + totalTaxAmt;
  const canEdit =
    (quoteFields.territory === terrCcn && quoteFields.territory !== "800100") ||
    (storeCcn === quoteFields.storeNumber && quoteFields.punchoutPublished !== true);

  const handleNoteChange = (e) => {
    e.target.style.height = "auto";
    e.target.style.height = `${e.target.scrollHeight}px`;
    setFloorPlanNote(e.target.value);
  };

  const openFloorImage = () => {
    if (!floorPlanImage) return;
    const newWindow = window.open();
    if (!newWindow) return;
    const img = newWindow.document.createElement("img");
    img.src = floorPlanImage;
    img.alt = "Floor plan image";
    newWindow.document.body.appendChild(img);
  };

  useEffect(() => {
    setFloorPlanNote(floorNote ?? "");
  }, [floorNote]);

  useEffect(() => {
    if ((floorPlanNote ?? "") === (floorNote ?? "")) return;
    const timer = setTimeout(() => {
      toast.promise(updateFloor({ quoteId, floorId, floorNotes: floorPlanNote }).unwrap(), {
        pending: "Saving note",
        success: "Note saved successfully",
        error: "Failed to save note",
      });
    }, 1000);
    return () => clearTimeout(timer);
  }, [floorPlanNote, floorNote, quoteId, floorId, updateFloor]);

  return (
    <Container
      fluid="xl"
      className="search-form-container border-top-0"
      style={{ border: "1px solid #d1d1d1", borderRadius: "0 0 8px 8px" }}
    >
      <QuoteSummaryFields quoteFields={quoteFields} />
      <QuoteFloorProducts />
      <QuoteFloorLabor />
      <div className="row col-12 col-md-12 col-lg-12 floor-grid justify-content-md-between">
        <div className="floormap-card row col-12 col-md-5 col-lg-5">
          <div className="m-2 pt-1">
            <span>Notes</span>
          </div>
          <textarea
            className="text-area-floor"
            id="floorPlanNote"
            value={floorPlanNote}
            onChange={handleNoteChange}
            readOnly={!canEdit}
            style={{ fieldSizing: "content" }}
          />
        </div>

        {floorPlanImage && (
          <div className="floormap-card row col-12 col-md-5 col-lg-3 align-items-center" style={{ height: "183px" }}>
            <div className="pt-2 pb-1">
              <span>Floor Plan Picture</span>
            </div>
            <div className="floormap-card__separator" />
            <div className="floormap-card__body">
              <span className="open-icon">
                <img src={openIcon} alt="Open floor plan image" onClick={openFloorImage} />
              </span>
              <img src={floorPlanImage} className="floormap-card__body__image" alt="Floor plan image" />
            </div>
          </div>
        )}

        <div className="total-card row col-12 col-md-5 col-lg-3 align-items-center" style={{ height: "183px" }}>
          <div className="total-card--main-header d-flex justify-content-between pb-2">
            <span>Total</span>
            <span>{currencyValue(totalAmt)}</span>
          </div>
          <div className="floormap-card__separator" />
          <div className="d-flex justify-content-between">
            <span className=" d-flex">
              <img src={productIcon} alt="product_icon" className="total-card--icon" />
              Material Total
            </span>
            <span className="d-flex">{currencyValue(totalProductsAmt)}</span>
          </div>
          <div className="d-flex justify-content-between">
            <span className="d-flex">
              <img src={productIcon} alt="product_icon" className="total-card--icon" />
              Labor Total
            </span>
            <span className="d-flex">{currencyValue(totalLaborAmt)}</span>
          </div>
          <div className="d-flex justify-content-between">
            <span className="d-flex">
              <img src={productIcon} alt="product_icon" className="total-card--icon" />
              Tax
            </span>
            <span className="d-flex">{currencyValue(totalTaxAmt)}</span>
          </div>
        </div>
      </div>
      <div className="d-flex justify-content-between pt-5">
        <Button variant="outline-primary" onClick={() => navigate(`/priceQuote/${quoteId}#quote-summary`)}>
          Back
        </Button>
      </div>
    </Container>
  );
};

export default FloorPlanDetails;
